/**
 * <reward-sticky-bar> — the reward message pinned to the bottom of the window.
 *
 * Reads the same tiers as the cart stepper and the same subtotal helper, so the
 * three surfaces cannot disagree about what is unlocked.
 *
 * Three things it does beyond showing a message:
 *
 *   TAP TO OPEN. It does not open the drawer itself — it clicks the theme's own
 *   cart trigger, so whatever that does (focus handling, overlay, body class)
 *   keeps happening exactly once, in one place.
 *
 *   "ADDED" FLASH. Briefly says "In the bag." ahead of the next reward, then
 *   settles back. All three strings are theme settings with {amount} and
 *   {reward}, so a new promo needs new tier labels, not new copy.
 *
 *   TAKING OVER FROM THE DRAWER. Optional, off by default. The drawer opens
 *   itself from inside renderContents, which every add path in the theme calls;
 *   rather than edit each of them, the method is wrapped once so it re-renders
 *   without toggling. Turning the setting off restores the old behaviour with
 *   no other change.
 */
(function () {

  /**
   * Store-currency money from the shop's own money format, e.g. "${{amount}}"
   * or "&euro;{{amount}}" (Shopify hands the symbol over HTML-encoded). The
   * theme has no Shopify.formatMoney, which is why this exists: the old
   * fallback hard-coded "$", so EU and UK showed dollars. Whole amounts drop
   * their decimals ("€30", not "€30.00") to keep the reward lines short.
   */
  function fbMoney(major, format) {
    var fmt = String(format || "${{amount}}");
    if (fmt.indexOf("&") !== -1) {
      var t = document.createElement("textarea");
      t.innerHTML = fmt;
      fmt = t.value;
    }
    var whole = Math.abs(major - Math.round(major)) < 0.005;
    function group(n, decimals, thousands, point) {
      var parts = Math.abs(n).toFixed(decimals).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (n < 0 ? "-" : "") + parts.join(point);
    }
    var styles = {
      amount: [",", "."],
      amount_no_decimals: [",", "."],
      amount_with_comma_separator: [".", ","],
      amount_no_decimals_with_comma_separator: [".", ","],
      amount_with_apostrophe_separator: ["'", "."],
      amount_with_space_separator: [" ", ","],
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      var s = styles[key] || styles.amount;
      var decimals = whole || key.indexOf("no_decimals") !== -1 ? 0 : 2;
      return group(major, decimals, s[0], s[1]);
    });
  }
  const DRAWER_TAG = "cart-drawer";

  function on(el, name) {
    return el.dataset[name] === "true";
  }

  // Same drawings as the cart stepper, so a reward looks the same in both.
  const ICONS = {
    bag: '<path d="M6 8h12l-1 12H7z"/><path d="M9 8V6a3 3 0 0 1 6 0v2"/>',
    truck:
      '<path d="M2 7h11v9H2z"/><path d="M13 10h4l4 3v3h-8z"/><circle cx="6.5" cy="18" r="1.8"/><circle cx="17" cy="18" r="1.8"/>',
    tick: '<path class="fb-sticky__tickpath" d="M5 12.5l4.5 4.5L19 7.5"/>',
    gift: '<path d="M3 10h18v10H3z"/><path d="M3 7h18v3H3z"/><path d="M12 7v13"/><path d="M12 7C10 7 8 6 8 4.5S10 3 12 7zM12 7c2 0 4-1 4-2.5S14 3 12 7z"/>',
  };

  function svg(name) {
    return (
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      (ICONS[name] || ICONS.gift) +
      "</svg>"
    );
  }

  /**
   * Stop the drawer opening itself on add, without touching the dozen callers
   * that ask it to. renderContents(state, isClosedCart) toggles the drawer when
   * the second argument is true; forcing it false leaves the re-render — which
   * we still want — and drops the slide-open.
   */
  function suppressDrawerOpening() {
    const Ctor = window.customElements && customElements.get(DRAWER_TAG);
    if (!Ctor || Ctor.prototype.__fbSuppressed) return;
    const original = Ctor.prototype.renderContents;
    if (typeof original !== "function") return;
    Ctor.prototype.renderContents = function (state, isClosedCart) {
      return original.call(this, state, false);
    };
    Ctor.prototype.__fbSuppressed = true;
  }

  class RewardStickyBar extends HTMLElement {
    connectedCallback() {
      try {
        this.tiers = JSON.parse(this.dataset.tiers || "[]");
      } catch (e) {
        this.tiers = [];
      }
      this.tiers = this.tiers
        .filter((t) => t && Number(t.amount) > 0)
        .sort((a, b) => Number(a.amount) - Number(b.amount));

      this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
      this.msgEl = this.querySelector("[data-sticky-message]");
      this.fillEl = this.querySelector("[data-sticky-fill]");
      this.countEl = this.querySelector("[data-sticky-count]");
      this.countWrap = this.querySelector("[data-sticky-count-wrap]");
      this.hitEl = this.querySelector("[data-sticky-open]");
      this.iconEl = this.querySelector("[data-sticky-icon]");
      // Survives nothing — the element is in the layout, not in a cart section
      // — but the count of unlocked tiers still has to be remembered between
      // cart changes to know which one is NEW.
      this.unlockedCount = null;

      if (!on(this, "showCount") && this.countWrap) this.countWrap.hidden = true;

      if (on(this, "replacesDrawer")) suppressDrawerOpening();

      if (on(this, "opensCart")) {
        this.hitEl.addEventListener("click", () => this.openCart());
      } else {
        this.hitEl.disabled = true;
      }

      this.apply(
        (Number(this.dataset.subtotal) || 0) / 100,
        Number(this.dataset.count) || 0,
      );

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) =>
          this.onCartUpdate(payload),
        );
        // Hiding behind an open drawer: the theme announces both edges.
        if (PUB_SUB_EVENTS.cartDrawerOpen) {
          this.onOpen = () => this.setDrawerOpen(true);
          this.onClose = () => this.setDrawerOpen(false);
          document.addEventListener(PUB_SUB_EVENTS.cartDrawerOpen, this.onOpen);
          document.addEventListener(PUB_SUB_EVENTS.cartDrawerClose, this.onClose);
        }
      }
      this.onDrawerRefresh = () => this.onCartUpdate();
      document.addEventListener("cart-drawer:refresh", this.onDrawerRefresh);
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener("cart-drawer:refresh", this.onDrawerRefresh);
      if (this.onOpen && typeof PUB_SUB_EVENTS !== "undefined") {
        document.removeEventListener(PUB_SUB_EVENTS.cartDrawerOpen, this.onOpen);
        document.removeEventListener(PUB_SUB_EVENTS.cartDrawerClose, this.onClose);
      }
      clearTimeout(this.addedTimer);
      clearTimeout(this.iconTimer);
    }

    /** Click the theme's own trigger rather than reimplementing "open". */
    openCart() {
      const trigger = document.querySelector(".wt-cart__trigger");
      if (trigger) {
        trigger.click();
        return;
      }
      window.location.href = this.dataset.cartUrl || "/cart";
    }

    setDrawerOpen(isOpen) {
      this.drawerOpen = isOpen;
      this.decideVisibility();
    }

    money(major) {
      return fbMoney(major, this.moneyFormat);
    }

    async onCartUpdate(payload) {
      // What was added, if anyone said. Adds from apps and from paths that do
      // not publish a payload simply update the numbers with no flash — better
      // than naming the wrong product.
      const added = this.addedName(payload);
      let cart;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        cart = await fetch(`${root}cart.js`).then((r) => r.json());
      } catch (e) {
        return;
      }
      const major =
        window.FBReward && window.FBReward.subtotalMajor
          ? window.FBReward.subtotalMajor(cart)
          : (cart.items || []).reduce(
              (sum, i) =>
                i.properties && i.properties._gift === "true"
                  ? sum
                  : sum + (i.final_line_price || 0),
              0,
            ) / 100;
      this.apply(major, cart.item_count || 0, added);
    }

    /** Pull a product name out of whatever the publisher handed us. */
    addedName(payload) {
      if (!payload) return null;
      const data = payload.cartData;
      if (!data) return payload.productTitle || null;
      if (data.items && data.items.length) {
        return data.items[0].product_title || data.items[0].title || null;
      }
      return data.product_title || data.title || null;
    }

    /**
     * Three strings cover every state and any number of tiers: just added,
     * a reward still to come, everything unlocked. Only the NEXT reward is
     * ever named; the tick does the job of saying one was just banked.
     */
    message(subtotal, addedProduct) {
      const next = this.tiers.find((t) => subtotal < Number(t.amount));
      if (!next) return [{ text: this.dataset.doneText || "All yours, babe." }];
      const template = addedProduct
        ? this.dataset.addedText || "In the bag. {amount} to {reward}."
        : this.dataset.pendingText || "{amount} to {reward}.";
      // The amount and the reward are the line's key information, so they
      // come back as their own parts and are painted in the highlight colour;
      // the words around them stay in the text colour.
      const values = {
        "{amount}": this.money(Number(next.amount) - subtotal),
        "{reward}": next.reward || next.label || "",
        "{product}": addedProduct || "",
      };
      return template
        .split(/(\{amount\}|\{reward\}|\{product\})/)
        .filter((piece) => piece !== "")
        .map((piece) =>
          piece in values
            ? { text: values[piece], key: piece !== "{product}" }
            : { text: piece },
        );
    }

    /** The next reward's icon; a tick once there is nothing left to earn. */
    setIcon(name, draw) {
      if (!this.iconEl) return;
      if (this.iconName !== name) {
        this.iconEl.innerHTML = svg(name);
        this.iconName = name;
      }
      this.iconEl.classList.toggle("is-drawing", !!draw);
    }

    apply(subtotal, count, addedProduct) {
      this.subtotal = subtotal;
      this.count = count;

      if (this.countEl) this.countEl.textContent = String(count);

      const max = Number(this.tiers[this.tiers.length - 1].amount) || 1;
      const pct =
        window.FBReward && window.FBReward.positionPct
          ? window.FBReward.positionPct(subtotal, this.tiers)
          : Math.min(subtotal / max, 1) * 100;
      if (this.fillEl) this.fillEl.style.width = pct + "%";

      this.rewardCopy = this.message(subtotal);

      const unlocked = this.tiers.filter((t) => subtotal >= Number(t.amount)).length;
      const isNew = this.unlockedCount !== null && unlocked > this.unlockedCount;
      this.unlockedCount = unlocked;
      this.classList.toggle("has-unlock", unlocked > 0);
      this.allDone = this.tiers.length > 0 && unlocked >= this.tiers.length;

      const next = this.tiers.find((t) => subtotal < Number(t.amount));
      const nextIcon = next ? next.icon || "gift" : "tick";
      clearTimeout(this.iconTimer);
      if (isNew && next) {
        // Just banked one: draw the tick, then move on to what's next.
        this.setIcon("tick", true);
        this.iconTimer = setTimeout(() => this.setIcon(nextIcon, false), 1600);
      } else {
        this.setIcon(nextIcon, isNew);
      }

      if (addedProduct) {
        this.flashAdded(addedProduct, subtotal);
      } else if (!this.flashing) {
        this.setMessage(this.rewardCopy, false);
      }

      this.decideVisibility();
    }

    /** Name what just went in, then settle back to the reward message. */
    flashAdded(product, subtotal) {
      clearTimeout(this.addedTimer);
      this.flashing = true;
      this.setMessage(this.message(subtotal, product), true);
      const seconds = parseFloat(this.dataset.addedSeconds) || 2;
      this.addedTimer = setTimeout(() => {
        this.flashing = false;
        this.setMessage(this.rewardCopy, false);
        // With "hide once everything is unlocked" on, the bar shows what
        // just went in, then goes.
        this.decideVisibility();
      }, seconds * 1000);
    }

    setMessage(parts, isAdded) {
      if (!this.msgEl) return;
      const sig = parts.map((p) => (p.key ? "*" : "") + p.text).join("|");
      if (this.msgSig === sig) return;
      this.msgSig = sig;
      this.msgEl.textContent = "";
      parts.forEach((p) => {
        if (p.key) {
          const span = document.createElement("span");
          span.className = "fb-sticky__key";
          span.textContent = p.text;
          this.msgEl.appendChild(span);
        } else {
          this.msgEl.appendChild(document.createTextNode(p.text));
        }
      });
      this.classList.toggle("is-added", !!isAdded);
      this.msgEl.classList.remove("is-swapping");
      void this.msgEl.offsetWidth;
      this.msgEl.classList.add("is-swapping");
    }

    /**
     * An empty cart shows nothing at all — there is no progress to report and a
     * permanent bar at the bottom of every page is a cost, not a feature.
     */
    decideVisibility() {
      const onCartPage =
        window.location.pathname === (this.dataset.cartUrl || "/cart");
      const hide =
        !this.count ||
        !this.tiers.length ||
        (on(this, "hideOnCart") && onCartPage) ||
        (on(this, "hideWithDrawer") && this.drawerOpen) ||
        // Every reward unlocked: nothing left to work towards (setting).
        (on(this, "hideWhenDone") && this.allDone && !this.flashing);
      this.hidden = hide;
      document.body.classList.toggle("fb-sticky-on", !hide);
    }
  }

  if (!customElements.get("reward-sticky-bar")) {
    customElements.define("reward-sticky-bar", RewardStickyBar);
  }
})();
